import { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Download, Upload } from 'lucide-react';
import ReactDOM from 'react-dom';
import { 
  ReactFlow, 
  Background, 
  Controls, 
  BackgroundVariant,
  ConnectionMode,
  ReactFlowProvider,
  useReactFlow
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';

import { useStore } from '../components/flow-builder/store';
import { CustomNodeComponent } from '../components/flow-builder/CustomNode';
import CustomEdge from '../components/flow-builder/CustomEdge';
import { FlowSidebar } from '../components/flow-builder/FlowSidebar';
import { Simulator } from '../components/flow-builder/Simulator';
import { Rocket, Save, Undo2, Redo2, Trash2, Sparkles, X, Workflow } from 'lucide-react';
import api from '../utils/api';
import { useToast } from '../context/ToastContext';
import DeleteConfirmModal from '../components/DeleteConfirmModal';

const nodeTypes = {
  custom: CustomNodeComponent,
};

const edgeTypes = {
  custom: CustomEdge,
};

const FlowContent = ({ flowId, flowDetail, onSave, navigate }) => {
  const { showToast } = useToast();
  const nodes = useStore(s => s.nodes);
  const edges = useStore(s => s.edges);
  const onNodesChange = useStore(s => s.onNodesChange);
  const onEdgesChange = useStore(s => s.onEdgesChange);
  const onConnect = useStore(s => s.onConnect);
  const loadFlowData = useStore(s => s.loadFlowData);
  const addNodeAtPosition = useStore(s => s.addNodeAtPosition);
  const setSelectedNodeId = useStore(s => s.setSelectedNodeId);
  const setNodes = useStore(s => s.setNodes);
  const setEdges = useStore(s => s.setEdges);
  const undo = useStore(s => s.undo);
  const redo = useStore(s => s.redo);
  const takeSnapshot = useStore(s => s.takeSnapshot);
  const _history = useStore(s => s._history);
  const _future = useStore(s => s._future);

  const canUndo = _history.length > 0;
  const canRedo = _future.length > 0;

  // Keyboard shortcuts for undo/redo
  useEffect(() => {
    const onKeyDown = (e) => {
      const isInput = ['INPUT', 'TEXTAREA'].includes(e.target.tagName) || e.target.isContentEditable;
      if (isInput) return;
      if ((e.ctrlKey || e.metaKey) && !e.shiftKey && e.key === 'z') {
        e.preventDefault();
        undo();
      } else if (
        ((e.ctrlKey || e.metaKey) && e.key === 'y') ||
        ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === 'z')
      ) {
        e.preventDefault();
        redo();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [undo, redo]);

  const reactFlowWrapper = useRef(null);
  const reactFlowInstance = useReactFlow();

  const onDragOver = useCallback((event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }, []);

  const onDrop = useCallback((event) => {
    event.preventDefault();

    const type = event.dataTransfer.getData('application/reactflow');
    if (typeof type === 'undefined' || !type) {
      return;
    }

    let position;
    if (reactFlowInstance.screenToFlowPosition) {
      position = reactFlowInstance.screenToFlowPosition({
        x: event.clientX,
        y: event.clientY,
      });
    } else {
      const reactFlowBounds = reactFlowWrapper.current.getBoundingClientRect();
      position = reactFlowInstance.project({
        x: event.clientX - reactFlowBounds.left,
        y: event.clientY - reactFlowBounds.top,
      });
    }

    addNodeAtPosition(type, position);
  }, [reactFlowInstance, addNodeAtPosition]);

  const [showSimulator, setShowSimulator] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [flowName, setFlowName] = useState('');
  const [flowDesc, setFlowDesc] = useState('');
  const [saving, setSaving] = useState(false);
  const [isClearModalOpen, setIsClearModalOpen] = useState(false);

  const [isAiModalOpen, setIsAiModalOpen] = useState(false);
  const [aiPrompt, setAiPrompt] = useState('');
  const [generating, setGenerating] = useState(false);

  const validateFlowData = (flow) => {
    if (!flow || !Array.isArray(flow.nodes) || !Array.isArray(flow.edges)) {
      return "Response missing nodes or edges";
    }
    for (const node of flow.nodes) {
      if (!node.id || !node.type || !node.data) {
        return "Generated node missing basic properties";
      }
      if (node.data.type === 'condition') {
        if (!Array.isArray(node.data.branches)) {
          return "Condition node missing branches list";
        }
        for (const branch of node.data.branches) {
          if (!branch.id || !branch.rule || typeof branch.rule !== 'object' || !branch.rule.field || !branch.rule.operator) {
            return "Condition node branch is missing rule structure";
          }
        }
      }
    }
    return null;
  };

  const handleAiGenerate = async () => {
    if (!aiPrompt.trim()) {
      showToast("Please describe your flow scenario", "error");
      return;
    }
    setGenerating(true);
    try {
      const response = await api.generateBotFlow(aiPrompt.trim());
      console.log('[AI Flow] Raw response:', JSON.stringify(response, null, 2));
      const validationError = validateFlowData(response);
      if (validationError) {
        console.warn('[AI Flow] Validation failed:', validationError, response);
        showToast(`Invalid generated flow: ${validationError}`, "error");
        return;
      }
      
      const normalizedResponse = {
        ...response,
        edges: (response.edges || []).map(e => ({
          ...e,
          markerEnd: e.markerEnd
            ? { ...e.markerEnd, type: e.markerEnd.type?.toLowerCase() }
            : undefined,
        })),
      };
      loadFlowData(normalizedResponse);
      showToast("Flow generated successfully!");
      setIsAiModalOpen(false);
      setAiPrompt('');
    } catch (err) {
      console.error('[AI Flow] Error:', err);
      showToast("Error generating flow: " + (err.message || "Request failed"), "error");
    } finally {
      setGenerating(false);
    }
  };

  useEffect(() => {
    if (flowDetail) {
      setFlowName(flowDetail.name || '');
      setFlowDesc(flowDetail.description || '');
      try {
        const parsed = JSON.parse(flowDetail.content);
        loadFlowData(parsed);
      } catch (err) {
        // Fallback for empty/new flow
        loadFlowData(null);
      }
    } else {
      setFlowName('New Flow');
      setFlowDesc('Interactive chatbot logic workflow');
      loadFlowData(null);
    }
  }, [flowDetail, loadFlowData]);

  const defaultEdgeOptions = useMemo(() => ({
    type: 'custom',
    animated: false,
    style: { strokeWidth: 3, stroke: '#e2e8f0' },
  }), []);

  const handleNodeClick = useCallback((event, node) => {
    setNodes(nodes.map(n => ({ ...n, selected: n.id === node.id })));
    setSelectedNodeId(node.id);
    setIsSidebarCollapsed(false);
  }, [nodes, setNodes, setSelectedNodeId, setIsSidebarCollapsed]);

  const handlePaneClick = useCallback(() => {
    setNodes(nodes.map(n => ({ ...n, selected: false })));
    setSelectedNodeId(null);
  }, [nodes, setNodes, setSelectedNodeId]);

  const handleCommit = async () => {
    if (!flowName.trim()) {
      showToast("Flow name cannot be empty", "error");
      return;
    }
    setSaving(true);
    try {
      const data = {
        name: flowName.trim(),
        description: flowDesc,
        content: JSON.stringify({ nodes, edges }),
        keywords: flowDetail?.keywords || [],
        is_active: true
      };
      await onSave(data);
    } finally {
      setSaving(false);
    }
  };

  const [isActive, setIsActive] = useState(false);
  const [togglingActive, setTogglingActive] = useState(false);

  useEffect(() => {
    if (flowDetail) {
      setIsActive(!!flowDetail.is_active);
    }
  }, [flowDetail]);

  const handleClearCanvas = () => {
    if (nodes.length === 0) return;
    setIsClearModalOpen(true);
  };

  const handleToggleActive = async () => {
    if (!flowId) return;
    setTogglingActive(true);
    try {
      const nextActive = !isActive;
      await api.toggleBotFlowActive(flowId, nextActive);
      setIsActive(nextActive);
      showToast(nextActive ? 'Flow activated successfully' : 'Flow deactivated successfully');
      
      // Trigger callback to refresh parent flowDetail state
      if (onSave) {
        onSave(null, true);
      }
      
      // Force Sidebar to refetch by slightly modifying url (e.g. adding / updating a timestamp query param or navigating back to trigger route render)
      navigate(`/flow-builder?id=${flowId}&_t=${Date.now()}`);
    } catch (err) {
      showToast('Error updating flow status', 'error');
    } finally {
      setTogglingActive(false);
    }
  };

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const handleDeleteFlow = async () => {
    setDeleting(true);
    try {
      await api.deleteBotFlow(flowId);
      showToast("Flow deleted successfully");
      setIsDeleteModalOpen(false);
      navigate("/flow-builder");
    } catch (err) {
      showToast("Error deleting flow", "error");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="flex flex-col w-full h-[calc(100vh-72px)] bg-slate-50 overflow-hidden relative">
      {/* Delete Confirmation Modal */}
      <DeleteConfirmModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleDeleteFlow}
        title="Delete Flow"
        message="Are you sure you want to permanently delete this visual flow? This action cannot be undone."
        confirmText="Delete Flow"
        isLoading={deleting}
      />

      {/* Visual Editor Header */}
      <div className="h-16 border-b border-slate-100 bg-white flex items-center justify-between px-6 shrink-0 z-10 shadow-sm">
        <div className="flex items-center gap-4 min-w-0">
          <input
            type="text"
            value={flowName}
            onChange={(e) => setFlowName(e.target.value)}
            className="text-sm font-black text-slate-800 uppercase tracking-wider bg-transparent border-b border-transparent hover:border-slate-200 focus:border-indigo-500 focus:outline-none px-1 py-0.5"
            placeholder="Flow Name"
          />

          {flowId && (
            <div className="flex items-center gap-3 pl-4 border-l border-slate-100">
              <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                {isActive ? 'Active' : 'Inactive'}
              </span>
              <label className="relative inline-flex items-center cursor-pointer group">
                <input
                  type="checkbox"
                  className="sr-only peer"
                  checked={isActive}
                  onChange={handleToggleActive}
                  disabled={togglingActive}
                />
                <div className="w-11 h-6 bg-slate-100 border-2 border-slate-200 rounded-full peer peer-checked:border-[#83bbe1] transition-all duration-300 after:content-[''] after:absolute after:top-[4px] after:left-[4px] after:bg-slate-300 after:rounded-full after:h-4 after:w-4 after:transition-all duration-300 peer-checked:after:translate-x-5 peer-checked:after:bg-[#83bbe1] peer-disabled:opacity-50"></div>
              </label>

              <button
                onClick={() => setIsDeleteModalOpen(true)}
                className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors ml-1"
                title="Delete Flow"
              >
                <Trash2 size={16} />
              </button>
            </div>
          )}
        </div>

        <div className="flex gap-3">
<button
            onClick={handleCommit}
            disabled={saving}
            className="flex items-center justify-center gap-2 px-6 py-2.5 bg-zinc-900 text-white rounded-full shadow-lg hover:bg-black font-bold text-xs transition-all active:scale-95 disabled:opacity-50"
            >
            <Save size={14} />
            {saving ? 'Saving...' : 'Commit Flow'}
            </button>
          <button
            onClick={() => setIsAiModalOpen(true)}
            className="flex items-center justify-center gap-2 px-6 py-2.5 bg-zinc-900 text-white rounded-full shadow-lg hover:bg-black font-bold text-xs transition-all active:scale-95 group"
          >
            <Sparkles size={14} className="group-hover:rotate-12 txransition-transform" />
            AI Generate
          </button>
          <button
            onClick={() => { setShowSimulator(true); setIsSidebarCollapsed(true); }}
            className="flex items-center justify-center gap-2 px-6 py-2.5 bg-zinc-900 text-white rounded-full shadow-lg hover:bg-black font-bold text-xs transition-all active:scale-95 group"
          >
            <Rocket size={14} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
            Test Simulation
          </button>

          <button
  onClick={() => {
    const { nodes, edges } = useStore.getState();
    const flowData = { nodes, edges };
    const json = JSON.stringify(flowData, null, 2);
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `flow-export-${Date.now()}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }}
    className="flex items-center justify-center gap-2 px-6 py-2.5 bg-zinc-900 text-white rounded-full shadow-lg hover:bg-black font-bold text-xs transition-all active:scale-95 disabled:opacity-50"
>
  <Upload size={14} />
  Export
</button>
<button
  onClick={() => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.json,application/json';
    input.onchange = (e) => {
      const file = e.target.files[0];
      if (file) {
        const reader = new FileReader();
        reader.onload = (event) => {
          try {
            const flowData = JSON.parse(event.target.result);
            if (flowData.nodes && flowData.edges) {
              useStore.getState().loadFlowData(flowData);
              showToast('Flow imported successfully');
            } else {
              showToast('Invalid flow file format', 'error');
            }
          } catch (err) {
            showToast('Failed to parse JSON file', 'error');
          }
        };
        reader.readAsText(file);
      }
    };
    input.click();
  }}
    className="flex items-center justify-center gap-2 px-6 py-2.5 bg-zinc-900 text-white rounded-full shadow-lg hover:bg-black font-bold text-xs transition-all active:scale-95 disabled:opacity-50"
>
  <Download size={14} />
  Import
</button>
        </div>
      </div>

      {/* Main workspace */}
      <div className="flex-1 flex min-h-0">
        <div className="flex-1 relative h-full" ref={reactFlowWrapper}>
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            nodeTypes={nodeTypes}
            edgeTypes={edgeTypes}
            defaultEdgeOptions={defaultEdgeOptions}
            connectionMode={ConnectionMode.Strict}
            nodesDraggable={true}
            nodesConnectable={true}
            elementsSelectable={true}
            fitView
            fitViewOptions={{ maxZoom: 0.65 }}
            proOptions={{ hideAttribution: true }}
            minZoom={0.2}
            maxZoom={2}
            onDragOver={onDragOver}
            onDrop={onDrop}
            onNodeClick={handleNodeClick}
            onPaneClick={handlePaneClick}
            onNodeDragStart={takeSnapshot}
          >
            <Background 
              variant={BackgroundVariant.Dots} 
              gap={24} 
              size={1} 
              color="#e2e8f0" 
            />
            <Controls className="!bg-white !border-slate-100 !shadow-lg !rounded-xl overflow-hidden" />

            {/* Undo / Redo floating buttons */}
            <div className="absolute bottom-4 left-16 z-10 flex items-center gap-1 bg-white border border-slate-100 shadow-lg rounded-2xl p-1">
              <button
                onClick={undo}
                title="Undo (Ctrl+Z)"
                disabled={!canUndo}
                className="flex items-center justify-center w-9 h-9 rounded-xl active:scale-95 hover:bg-slate-50 text-slate-600 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <Undo2 size={16}/>
              </button>
              <div className="w-px h-5 bg-slate-100" />
              <button
                onClick={redo}
                title="Redo (Ctrl+Y)"
                disabled={!canRedo}
                className="flex items-center justify-center w-9 h-9 rounded-xl active:scale-95 hover:bg-slate-50 text-slate-600 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <Redo2 size={16} />
              </button>
            </div>
          </ReactFlow>

          {/* Clear Canvas button in the top-right corner of the canvas */}
          <div className="absolute top-4 right-4 z-10">
            <button
              onClick={handleClearCanvas}
              disabled={nodes.length === 0}
              title="Clear Canvas"
              className="flex items-center justify-center gap-2 px-4 py-2.5 bg-white border border-slate-200 hover:bg-rose-50 hover:border-rose-200 hover:text-rose-600 text-slate-600 rounded-xl shadow-lg font-bold text-xs transition-all active:scale-95 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-white disabled:hover:border-slate-200 disabled:hover:text-slate-600"
            >
              <Trash2 size={14} />
              Clear Canvas
            </button>
          </div>
        </div>

        <FlowSidebar
          isCollapsed={isSidebarCollapsed}
          onToggle={() => setIsSidebarCollapsed(prev => !prev)}
          onNodeSelect={() => setIsSidebarCollapsed(false)} 
        />
      </div>
      
      {showSimulator && <Simulator onClose={() => setShowSimulator(false)} />}

      <DeleteConfirmModal
        isOpen={isClearModalOpen}
        onClose={() => setIsClearModalOpen(false)}
        onConfirm={() => {
          takeSnapshot();
          setNodes([]);
          setEdges([]);
          setSelectedNodeId(null);
          setIsClearModalOpen(false);
        }}
        title="Clear Canvas"
        message="Are you sure you want to clear the entire canvas? This will permanently delete all nodes and connections from the editor workspace."
        confirmText="Clear canvas"
        cancelText="Keep Design"
      />

      {isAiModalOpen && ReactDOM.createPortal(
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 sm:p-6 font-['Inter',sans-serif]">
          <div 
            className="fixed inset-0 bg-zinc-900/50 backdrop-blur-sm animate-fadeIn"
            onClick={!generating ? () => setIsAiModalOpen(false) : undefined}
          />
          <div className="relative bg-white w-full max-w-md rounded-2xl shadow-2xl shadow-zinc-900/20 overflow-hidden border border-zinc-200/90 animate-slideIn">
            <button 
              onClick={() => setIsAiModalOpen(false)}
              disabled={generating}
              className="absolute top-4 right-4 p-1.5 text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100 rounded-lg transition-colors disabled:opacity-50"
              aria-label="Close dialog"
            >
              <X size={16} />
            </button>
            <div className="p-5 sm:p-6 space-y-4">
              <div className="flex items-start gap-3.5">
                <Sparkles className="text-purple-600 shrink-0" size={18} />
                <div className="min-w-0 flex-1 pr-6">
                  <h3 className="text-sm sm:text-base font-semibold text-zinc-900 tracking-tight">
                    AI Flow Generator
                  </h3>
                  <p className="text-xs text-zinc-500 mt-1 leading-relaxed">
                    Describe your conversation flow. The AI will design nodes, conditional branches, and connections automatically.
                  </p>
                </div>
              </div>

              <div>
                <textarea
                  value={aiPrompt}
                  onChange={(e) => setAiPrompt(e.target.value)}
                  disabled={generating}
                  placeholder="Example: Ask the user for their name, then email. Call a webhook to submit lead. If success, thank them. If fail, escalate to human agent."
                  className="w-full h-28 px-3.5 py-2.5 text-xs text-zinc-900 placeholder:text-zinc-400 border border-zinc-200 rounded-xl focus:outline-none focus:ring-1 focus:ring-zinc-900 focus:border-zinc-900 transition-all resize-none disabled:opacity-50"
                />
              </div>

              <div className="flex items-center justify-end gap-2.5 pt-3 border-t border-zinc-100">
                <button
                  type="button"
                  onClick={() => setIsAiModalOpen(false)}
                  disabled={generating}
                  className="px-3.5 py-2 text-xs font-medium text-zinc-700 bg-zinc-100 hover:bg-zinc-200 rounded-lg transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleAiGenerate}
                  disabled={generating || !aiPrompt.trim()}
                  className="px-4 py-2 text-xs font-medium text-white bg-zinc-900 hover:bg-zinc-800 active:bg-black rounded-lg transition-colors shadow-xs flex items-center justify-center gap-1.5 disabled:opacity-50"
                >
                  {generating ? (
                    <>
                      <div className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      <span>Generating...</span>
                    </>
                  ) : (
                    <>
                      <Sparkles size={13} />
                      <span>Generate Flow</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default function FlowBuilder() {
  const location = useLocation();
  const navigate = useNavigate();
  const { showToast } = useToast();

  const [flowDetail, setFlowDetail] = useState(null);
  const [loading, setLoading] = useState(true);

  // Extract ID from ?id=...
  const queryParams = new URLSearchParams(location.search);
  const flowId = queryParams.get('id');

  const fetchFlow = useCallback(async () => {
    if (!flowId) {
      setFlowDetail(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const detail = await api.getBotFlowDetail(flowId);
      setFlowDetail(detail);
    } catch (err) {
      showToast('Error loading business logic detail', 'error');
    } finally {
      setLoading(false);
    }
  }, [flowId, showToast]);

  useEffect(() => {
    fetchFlow();
  }, [fetchFlow]);

  const handleSave = async (data, refreshOnly = false) => {
    if (refreshOnly) {
      fetchFlow();
      return;
    }
    try {
      if (flowId) {
        await api.updateBotFlow(flowId, data);
        showToast('Flow synchronized successfully');
        navigate(`/flow-builder?id=${flowId}&_t=${Date.now()}`);
      } else {
        const res = await api.saveBotFlow(data);
        showToast('Flow created successfully');
        if (res && res.id) {
          navigate(`/flow-builder?id=${res.id}&_t=${Date.now()}`);
        }
      }
      fetchFlow();
    } catch (err) {
      showToast('Error saving flow', 'error');
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-[calc(100vh-72px)] bg-slate-50 gap-3">
        <div className="animate-spin rounded-full h-8 w-8 border-3 border-zinc-900 border-t-transparent"></div>
        <span className="text-xs font-black text-zinc-400 uppercase tracking-wider font-semibold">Loading Canvas</span>
      </div>
    );
  }

  if (!flowId && location.pathname === "/flow-builder/new") {
    // Treat as new creation mode
    return (
      <ReactFlowProvider>
        <FlowContent flowId={null} flowDetail={null} onSave={handleSave} navigate={navigate} />
      </ReactFlowProvider>
    );
  }

  if (!flowId) {
    return (
      <div className="h-[calc(100vh-72px)] flex flex-col items-center justify-center p-12 bg-slate-50/50 text-center">
        <div className="w-16 h-16 rounded-2xl bg-white border border-slate-200/80 shadow-sm flex items-center justify-center mx-auto mb-6 text-indigo-600">
          <Workflow size={30} strokeWidth={1.8} />
        </div>
        <h2 className="text-xl font-black text-slate-800 uppercase tracking-tighter">Visual Flow Builder</h2>
        <p className="text-slate-400 text-sm max-w-sm mx-auto leading-relaxed mt-2">
          Select an existing visual flow from the workspace list on the left, or create a brand new logic workflow.
        </p>
      </div>
    );
  }

  return (
    <ReactFlowProvider>
      <FlowContent flowId={flowId} flowDetail={flowDetail} onSave={handleSave} navigate={navigate} />
    </ReactFlowProvider>
  );
}
